import { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Divider, Link, Rating, Stack, Typography,
} from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { api } from '../api/client';
import type { ApiError } from '../api/client';
import type { PlaceDetails } from '../api/types';
import { PhotoStrip } from './PlacePhotos';

/**
 * What is known about one place: photographs, rating, hours, address.
 *
 * The map already shows where a place is; what somebody actually wants
 * before committing an hour of their day is whether it is any good and
 * whether it will be open when they arrive. Neither exists in
 * OpenStreetMap, so both come from the enrichment cache — and a place the
 * cache has not reached yet still shows what OSM knows, saying plainly that
 * there is nothing more. An empty panel would read as a failure; this reads
 * as a gap.
 *
 * The record alone, with no frame around it: it is read in a dialog opened
 * from a stop, and in a tab of the place browser beside the coverage report
 * and the map. Both want the same answer, and one copy of it is the only
 * way they keep giving the same one.
 */

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface OpeningHours {
  openNow?: boolean;
  weekdayDescriptions?: string[];
}

/** Today's line, so the common question is answered without reading a table. */
function todayLine(hours: OpeningHours): string | null {
  const descriptions = hours.weekdayDescriptions;
  if (!descriptions?.length) return null;

  // Sunday is 0 in JS but last in Google's list, which starts on Monday.
  const todayName = DAY_ORDER[(new Date().getDay() + 6) % 7];
  return descriptions.find((d) => d.startsWith(`${todayName}:`)) ?? null;
}

/**
 * Reads one place's record, and says what went wrong where it could not.
 *
 * Cleared the moment the place changes, so reopening on another stop never
 * shows the last one's details for a frame.
 */
export function usePlaceDetails(poiId: string | null): {
  details: PlaceDetails | null;
  error: string | null;
} {
  const [details, setDetails] = useState<PlaceDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poiId) {
      setDetails(null);
      setError(null);

      return undefined;
    }

    setDetails(null);
    setError(null);

    let cancelled = false;
    void (async () => {
      try {
        const result = await api.placeDetails(poiId);
        if (!cancelled) setDetails(result);
      } catch (err) {
        if (!cancelled) setError((err as ApiError).message);
      }
    })();

    return () => { cancelled = true; };
  }, [poiId]);

  return { details, error };
}

export function PlaceDetailsBody({
  details,
  error,
}: {
  details: PlaceDetails | null;
  error: string | null;
}) {
  const external = details?.external ?? null;
  const hours: OpeningHours | null = external?.openingHours ?? null;

  return (
    <>
      {error && <Alert severity="error" variant="outlined">{error}</Alert>}

      {!details && !error && (
        <Stack sx={{ alignItems: 'center', py: 4 }}><CircularProgress size={24} /></Stack>
      )}

      {details && (
        <Stack spacing={2}>
          <PhotoStrip photos={details.photos} />

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={details.poi.categoryLabel} />
            {external?.primaryType && external.primaryType !== details.poi.categoryKey && (
              <Chip size="small" variant="outlined" label={external.primaryType.replace(/_/g, ' ')} />
            )}
            {external?.businessStatus === 'CLOSED_PERMANENTLY' && (
              <Chip size="small" color="error" label="Permanently closed" />
            )}
          </Stack>

          {external?.rating != null && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Rating value={external.rating} precision={0.1} size="small" readOnly />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {external.rating.toFixed(1)}
                </Typography>
                {external.userRatingCount != null && (
                  <Typography variant="caption" color="text.secondary">
                    {external.userRatingCount.toLocaleString('en-US')} reviews
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {details.poi.address && (
            <Stack direction="row" spacing={1}>
              <PlaceOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                {details.poi.address}
              </Typography>
            </Stack>
          )}

          {hours && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                <ScheduleIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Opening hours</Typography>
                {hours.openNow !== undefined && (
                  <Chip
                    size="small"
                    color={hours.openNow ? 'success' : 'default'}
                    label={hours.openNow ? 'Open now' : 'Closed now'}
                  />
                )}
              </Stack>
              <Stack sx={{ pl: 4 }}>
                {(hours.weekdayDescriptions ?? []).map((line) => {
                  const isToday = line === todayLine(hours);

                  return (
                    <Typography
                      key={line}
                      variant="caption"
                      sx={{ fontWeight: isToday ? 700 : 400 }}
                      color={isToday ? 'text.primary' : 'text.secondary'}
                    >
                      {line}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            Planned stay {details.poi.dwellMinutes} min · from the{' '}
            {details.poi.categoryLabel.toLowerCase()} category default
          </Typography>

          {/*
            Three different silences, and telling somebody the wrong one is
            worse than saying nothing at all. Only the first is "we have not
            got round to it"; the other two are answers.
          */}
          {!external && (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              {details.lookupOutcome === 'not-found'
                ? 'We looked this place up and the provider had no record of it, '
                  + 'so there is no rating or opening hours to show.'
                : details.lookupOutcome === 'unconfirmed'
                  ? 'We could not confirm this place with the provider — what came '
                    + 'back looked like somewhere else, so its rating and hours are '
                    + 'not shown.'
                  : 'No rating or opening hours for this place yet. Details are '
                    + 'looked up as places come up in planning.'}
            </Alert>
          )}

          {details.attribution && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                {details.attribution}. Opening hours can change — check with the{' '}
                {/*
                  Addressed by coordinate, with the provider's own id when
                  we have a trusted one.

                  Sending the NAME as the query asks Google to guess, and it
                  guesses badly: "DC USA" is a shopping mall in Columbia
                  Heights, and a text search for it lands on Washington, D.C.
                  Our coordinate is the one the route was planned around, so
                  it is the only unambiguous thing we hold.
                */}
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${
                    details.poi.lat},${details.poi.lng}${
                    external?.externalId
                      ? `&query_place_id=${external.externalId}`
                      : ''}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  place itself
                </Link>{' '}
                before relying on them.
              </Typography>
            </>
          )}
        </Stack>
      )}
    </>
  );
}
